import "@/App.css";
import { startAppUsageTracking } from "@/services/appUsage";
import { startPushOpenTracking } from "@/services/pushOpenTracking";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, lazy, Suspense } from "react";
import { toast } from "sonner";
import { hasCapability } from "@/config/capabilities";

import ConnectionStatusBanner from "@/components/ConnectionStatusBanner";
import { PageLoading, PageLoadRecovery } from "@/components/StartupRecovery";
import { resolveTenantHost } from "@/utils/resolveTenantHost";
import {
    LuckyVaultGetAppPage,
    LuckyVaultInstallPrompt,
    LuckyVaultLaunch,
    LuckyVaultMetadata,
    isHittingSlotsHost,
} from "@/components/LuckyVaultBrandExperience";

// Chat notification sound — global gesture unlock so AudioContext is
// allowed to play once the user has interacted with the page.
import { unlockNotificationSound } from "@/chat/notificationSound";

// Context
import { AppProvider } from "@/context/AppContext";
import { AdminProvider } from "@/context/AdminContext";
import { PermissionsProvider } from "@/context/PermissionsContext";

// Preset engine
import { PresetProvider } from "./presets/context/PresetProvider";
import PresetShell from "./presets/components/PresetShell";
import PresetPage from "./presets/components/PresetPage";

import AdminRouteGuard from "@/components/admin/AdminRouteGuard";

// Client UI V3 theme (light, clubgg-style) — used by /v/:slug client portal
import "@/presets/themes/client-v3.css";

const lazyDefault = (loader) => lazy(loader);
const lazyNamed = (loader, exportName) =>
    lazy(() => loader().then((module) => ({ default: module[exportName] })));

// Route families are intentionally loaded on demand. A player opening a
// storefront should not parse the superadmin, vendor-admin and support
// consoles, and an admin should not pay the cost of the complete player app.
const AdminLayout = lazyNamed(
    () => import("@/components/admin/AdminLayout"),
    "AdminLayout",
);
const FanytelVerifyPhoneModal = lazyDefault(
    () => import("@/components/FanytelVerifyPhoneModal"),
);
const SignupPromptDialog = lazyDefault(
    () => import("@/components/SignupPromptDialog"),
);
const AutoPushNotificationPrompt = lazyDefault(
    () => import("@/components/AutoPushNotificationPrompt"),
);

const LandingPage = lazyDefault(() => import("@/pages/LandingPage"));
const HomePage = lazyDefault(() => import("@/pages/HomePage"));
const WalletPage = lazyDefault(() => import("@/pages/WalletPage"));
const GatewayDepositStatus = lazyDefault(
    () => import("@/pages/GatewayDepositStatus"),
);
const GamesPage = lazyDefault(() => import("@/pages/GamesPage"));
const GameDetailPage = lazyDefault(() => import("@/pages/GameDetailPage"));
const ReferralsPage = lazyDefault(() => import("@/pages/ReferralsPage"));
const AchievementsPage = lazyDefault(() => import("@/pages/AchievementsPage"));
const InboxPage = lazyDefault(() => import("@/pages/InboxPage"));
const ProfilePage = lazyDefault(() => import("@/pages/ProfilePage"));
const TermsPage = lazyDefault(() => import("@/pages/TermsPage"));
const PrivacyPage = lazyDefault(() => import("@/pages/PrivacyPage"));
const TransactionHistoryPage = lazyDefault(
    () => import("@/pages/TransactionHistoryPage"),
);
const PublicCheckoutPage = lazyDefault(
    () => import("@/pages/PublicCheckoutPage"),
);
const VerificationPage = lazyDefault(() => import("@/pages/VerificationPage"));
const MagicLinkPage = lazyDefault(() => import("@/pages/auth/MagicLinkPage"));
const SetPasswordPage = lazyDefault(() => import("@/pages/auth/SetPasswordPage"));
const ForgotPasswordPage = lazyDefault(
    () => import("@/pages/auth/ForgotPasswordPage"),
);

const loadClubsApp = () => import("@/pages/clubs/ClubsApp");
const ClubsApp = lazyDefault(loadClubsApp);
const UnifiedLoginPage = lazyNamed(loadClubsApp, "LoginPage");
const UnifiedSignupPage = lazyNamed(loadClubsApp, "SignupPage");

const HowItWorksPage = lazyDefault(
    () => import("@/pages/rules/HowItWorksPage"),
);
const ReferralRulesPage = lazyDefault(
    () => import("@/pages/rules/ReferralRulesPage"),
);
const TierSystemPage = lazyDefault(() => import("@/pages/rules/TierSystemPage"));
const BonusesPage = lazyDefault(() => import("@/pages/rules/BonusesPage"));
const PaymentsPage = lazyDefault(() => import("@/pages/rules/PaymentsPage"));

const loadAdminPages = () => import("@/pages/admin");
const adminPage = (name) => lazyNamed(loadAdminPages, name);
const AdminLoginPage = adminPage("AdminLoginPage");
const Dashboard = adminPage("Dashboard");
const UsersPage = adminPage("UsersPage");
const PaymentMethodsPage = adminPage("PaymentMethodsPage");
const GamesAdminPage = adminPage("GamesAdminPage");
const GameProvidersPage = adminPage("GameProvidersPage");
const IntegrationsPage = adminPage("IntegrationsPage");
const WebhookEventsPage = adminPage("WebhookEventsPage");
const AuditLogsPage = adminPage("AuditLogsPage");
const RulesPage = adminPage("RulesPage");
const PromotionsPage = adminPage("PromotionsPage");
const SecurityPage = adminPage("SecurityPage");
const PermissionsPage = adminPage("PermissionsPage");
const FinancePage = adminPage("FinancePage");
const TransactionsPage = adminPage("TransactionsPage");
const OpsConsolePage = adminPage("OpsConsolePage");
const AchievementsAdminPage = adminPage("AchievementsAdminPage");
const ReferralTiersPage = adminPage("ReferralTiersPage");
const ActivityCommissionsPage = adminPage("ActivityCommissionsPage");
const LevelsPage = adminPage("LevelsPage");
const WalletRulesPage = adminPage("WalletRulesPage");
const ReferralAnalyticsPage = adminPage("ReferralAnalyticsPage");
const ReferralPayoutForecastPage = adminPage("ReferralPayoutForecastPage");
const MessagingCenterPage = adminPage("MessagingCenterPage");
const VoidedAmountsPage = adminPage("VoidedAmountsPage");
const AliasMigrationPage = adminPage("AliasMigrationPage");
const PermissionsMatrixPage = adminPage("PermissionsMatrixPage");
const StaffManagementPage = adminPage("StaffManagementPage");
const WebhooksPage = adminPage("WebhooksPage");
const OrdersPage = adminPage("OrdersPage");
const AdminChatPage = adminPage("AdminChatPage");
const TelegramTargetsPage = adminPage("TelegramTargetsPage");
const CategoriesAdminPage = adminPage("CategoriesAdminPage");
const NewDepositorsPage = adminPage("NewDepositorsPage");
const VendorsPage = adminPage("VendorsPage");
const VendorDetailPage = adminPage("VendorDetailPage");
const AdminPlatformTelegramPage = adminPage("AdminPlatformTelegramPage");
const VendorAnalyticsPage = adminPage("VendorAnalyticsPage");
const VendorLeaderboardPage = adminPage("VendorLeaderboardPage");
const AnalyticsCohortPage = adminPage("AnalyticsCohortPage");
const AnalyticsFunnelPage = adminPage("AnalyticsFunnelPage");
const PlayerMoneyPage = adminPage("PlayerMoneyPage");
const ManualFreeCreditsPage = adminPage("ManualFreeCreditsPage");
const GatewayDisputesPage = adminPage("GatewayDisputesPage");
const SafeWalletReviewsPage = adminPage("SafeWalletReviewsPage");

const AdminTelegramPage = lazyDefault(
    () => import("@/pages/admin/AdminTelegramPage"),
);
const AnnouncementsPage = lazyDefault(
    () => import("@/pages/admin/AnnouncementsPage"),
);
const AdminPlatformLedgerPage = lazyDefault(
    () => import("@/pages/admin/AdminPlatformLedgerPage"),
);
const AdminVendorTopupsPage = lazyDefault(
    () => import("@/pages/admin/AdminVendorTopupsPage"),
);

const SupportRouteGuard = lazyDefault(
    () => import("@/components/support/SupportRouteGuard"),
);
const SupportLayout = lazyDefault(() => import("@/components/support/SupportLayout"));
const SupportChatShell = lazyDefault(
    () => import("@/pages/support/SupportChatShell"),
);
const VendorPanel = lazyDefault(() => import("@/pages/vendor/VendorPanel"));
const ClientPortal = lazyDefault(
    () => import("@/pages/vendor-client/ClientPortal"),
);
const PlansPage = lazyDefault(() => import("@/pages/PlansPage"));

// The native Android shell should behave like an app, not a selectable or
// zoomable web document. Keep normal selection/callouts in actual editors so
// typing, cursor placement and paste still work in forms and chat.
if (
    typeof navigator !== "undefined" &&
    /(?:LuckyVault|HittingSlots)Android\//i.test(navigator.userAgent)
) {
    document.documentElement.classList.add("lucky-vault-native-app");

    if (!document.getElementById("lucky-vault-native-interaction-guards")) {
        const nativeInteractionStyle = document.createElement("style");
        nativeInteractionStyle.id = "lucky-vault-native-interaction-guards";
        nativeInteractionStyle.textContent = `
            html.lucky-vault-native-app,
            html.lucky-vault-native-app body,
            html.lucky-vault-native-app #root {
                touch-action: pan-x pan-y;
                -webkit-user-select: none;
                user-select: none;
                -webkit-touch-callout: none;
                -webkit-tap-highlight-color: transparent;
            }

            html.lucky-vault-native-app body *,
            html.lucky-vault-native-app body *::before,
            html.lucky-vault-native-app body *::after {
                -webkit-user-select: none;
                user-select: none;
                -webkit-touch-callout: none;
                -webkit-tap-highlight-color: transparent;
            }

            html.lucky-vault-native-app :where(
                input,
                textarea,
                select,
                option,
                [contenteditable="true"],
                [contenteditable=""],
                [role="textbox"]
            ) {
                -webkit-user-select: text;
                user-select: text;
                -webkit-touch-callout: default;
            }

            html.lucky-vault-native-app :focus:not(:focus-visible) {
                outline: none !important;
            }
        `;
        document.head.appendChild(nativeInteractionStyle);
    }
}

// Lazy loaded admin pages for better performance
const GameAnalyticsPage = lazy(() => import("@/pages/admin/GameAnalyticsPage"));
const UsersAnalyticsPage = lazy(
    () => import("@/pages/admin/UsersAnalyticsPage"),
);
const UserProfitabilityPage = lazy(
    () => import("@/pages/admin/UserProfitabilityPage"),
);
// VerifiedPurchasesPage removed 2026-05-08 — not useful; data still queryable
// via the `/api/admin/analytics/verified-purchases` endpoint if needed.
const VerifiedUsersPage = lazy(() => import("@/pages/admin/VerifiedUsersPage"));
const AllClientsLeaderboardsPage = lazy(
    () => import("@/pages/admin/AllClientsLeaderboardsPage"),
);
const AdminLeaderboardConfigPage = lazy(
    () => import("@/pages/admin/AdminLeaderboardConfigPage"),
);
const AdminWinnersFeedPage = lazy(
    () => import("@/pages/admin/AdminWinnersFeedPage"),
);
const FrontendAppearancePresetsPage = lazy(
    () => import("@/pages/admin/FrontendAppearancePresetsPage"),
);

// Lazy loaded client pages
const ChatPage = lazy(() => import("@/chat/ChatPage"));

// Pages that hide bottom nav
const hideNavPaths = [
    "/landing",
    "/terms",
    "/privacy",
    "/plans",
    "/login",
    "/signup",
    "/auth/login",
    "/auth/signup",
    "/auth/magic-link",
    "/auth/set-password",
    "/rules/how-it-works",
    "/rules/referrals",
    "/rules/tiers",
    "/rules/bonuses",
    "/rules/payments",
    "/c",
];

// Protected Admin Layout Wrapper
function ProtectedAdminLayout() {
    return (
        <AdminRouteGuard>
            <Suspense fallback={<RouteChunkFallback />}>
                <AdminLayout />
            </Suspense>
        </AdminRouteGuard>
    );
}

function RouteChunkFallback({ tenant = false }) {
    if (tenant) return <PageLoading quiet />;
    return (
        <div
            className={`min-h-[100dvh] ${tenant ? "bg-[#020906]" : "bg-slate-50"}`}
            aria-busy="true"
            aria-label="Loading page"
        />
    );
}

const AUTH_REDIRECT_PATHS = new Set([
    "/login",
    "/signup",
    "/auth/login",
    "/auth/signup",
    "/auth/forgot-password",
    "/auth/magic-link",
    "/auth/set-password",
    "/superadmin/login",
    "/clubs/login",
    "/clubs/signup",
    "/vendor/login",
]);

function readStoredJson(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

const ADMIN_ROLES = new Set(["superadmin", "admin", "moderator", "support"]);
const VENDOR_ROLES = new Set(["vendor", "vendor_admin"]);
// Rick Elite Spins is both an active tenant storefront and the client's
// administration origin. Keep its public routes on the tenant router while
// allowing only the authenticated /superadmin surface through the shared
// admin router. Other tenant domains retain the existing isolation screen.
const TENANT_SUPERADMIN_HOSTS = new Set([
    "rickelitespins.com",
    "www.rickelitespins.com",
]);
const TENANT_DOMAIN_ENTRY_PATHS = new Set([
    "/",
    // PATCH-tenant-entry-loop: "/clubs" is intentionally NOT an entry path. ClubDeeplink
    // bounces /clubs/<slug> -> /clubs to render the active club INLINE, so
    // /clubs is the resting place. Listing it here made the tenant resolver
    // redirect /clubs -> /clubs/<vendor>, fighting that bounce and causing an
    // infinite redirect loop on vendor custom domains (feaslots.com).
    "/login",
    "/signup",
    "/clubs/login",
    "/clubs/signup",
]);

function isTenantCandidateHost(hostname) {
    if (!hostname) return false;
    const host = hostname.toLowerCase().split(":")[0];
    return (
        host !== "multiple.games" &&
        host !== "www.multiple.games" &&
        host !== "zone.multiple.games" &&
        host !== "localhost" &&
        !host.startsWith("127.") &&
        !host.startsWith("10.") &&
        !host.startsWith("192.168.") &&
        !host.endsWith(".local")
    );
}

function allowsTenantSuperadminRoute(hostname, pathname) {
    if (!hostname || !pathname) return false;
    const host = hostname.toLowerCase().split(":")[0];
    const isSuperadminPath =
        pathname === "/superadmin" || pathname.startsWith("/superadmin/");
    return isSuperadminPath && TENANT_SUPERADMIN_HOSTS.has(host);
}

function shouldRedirectTenantEntry(location) {
    const path = location.pathname || "/";
    return TENANT_DOMAIN_ENTRY_PATHS.has(path);
}

function TenantLegacyClubsRedirect() {
    const location = useLocation();
    const path = location.pathname || "/clubs";
    const params = new URLSearchParams(location.search || "");
    params.delete("join");
    params.delete("code");
    const cleanSearch = params.toString() ? `?${params.toString()}` : "";
    const cleanPath = (() => {
        if (path === "/clubs/login") return "/login";
        if (path === "/clubs/signup") return "/signup";
        if (path === "/clubs/me") return "/me";
        if (path === "/clubs/verification") return "/verification";
        if (path === "/clubs/credits" || path === "/clubs/wallet") return "/credits";
        if (path === "/clubs/achievements" || path === "/clubs/rewards") return "/rewards";
        if (path === "/clubs/leaderboards") return "/leaders";
        if (path === "/clubs/chats") return "/chat";
        if (path === "/clubs/history") return "/history";
        if (path.startsWith("/clubs/history/")) {
            return path.replace(/^\/clubs/, "");
        }
        if (path === "/clubs" || /^\/clubs\/[^/]+$/.test(path)) return "/";
        return "/";
    })();
    return <Navigate to={`${cleanPath}${cleanSearch}`} replace />;
}

function storedRole(user) {
    return (user?.role || "").toLowerCase();
}

function hasValidAdminSession() {
    return !!(
        localStorage.getItem("adminSessionId") &&
        ADMIN_ROLES.has(storedRole(readStoredJson("adminUser")))
    );
}

function hasValidVendorSession() {
    return !!(
        localStorage.getItem("vendor_token") ||
        localStorage.getItem("vendorToken")
    );
}

function isAuthPath(path) {
    if (!path) return false;
    if (AUTH_REDIRECT_PATHS.has(path)) return true;
    return (
        path.startsWith("/auth/") ||
        path === "/admin/login" ||
        path === "/superadmin/login" ||
        path === "/vendor/login"
    );
}

function getLoggedInRedirect(location) {
    const params = new URLSearchParams(location.search || "");
    const next = params.get("next");
    if (next && next.startsWith("/") && !isAuthPath(next)) {
        return next;
    }

    const path = location.pathname || "";
    const regularSession = localStorage.getItem("sessionId");
    const regularUser = readStoredJson("user");
    const regularRole = storedRole(regularUser);

    const adminDestination =
        hasValidAdminSession() ||
        (regularSession && ADMIN_ROLES.has(regularRole))
            ? (() => {
                  const adminRole =
                      storedRole(readStoredJson("adminUser")) || regularRole;
                  if (adminRole === "support") return "/support";
                  return "/superadmin";
              })()
            : null;

    const vendorDestination =
        hasValidVendorSession() ||
        (regularSession && VENDOR_ROLES.has(regularRole))
            ? "/admin"
            : null;

    const clientDestination =
        localStorage.getItem("clubsClientToken") ||
        (regularSession &&
            !ADMIN_ROLES.has(regularRole) &&
            !VENDOR_ROLES.has(regularRole))
            ? "/clubs"
            : null;

    if (path === "/superadmin/login") return adminDestination;
    if (path === "/admin/login" || path === "/vendor/login") return vendorDestination;
    if (
        path === "/login" ||
        path === "/signup" ||
        path === "/clubs/login" ||
        path === "/clubs/signup" ||
        path.startsWith("/auth/")
    ) {
        return clientDestination;
    }

    if (clientDestination) {
        return clientDestination;
    }
    return adminDestination || vendorDestination;
}

function RedirectIfLoggedIn({ children }) {
    const location = useLocation();
    const destination = getLoggedInRedirect(location);
    if (destination) {
        return <Navigate to={destination} replace />;
    }
    return children;
}

function TenantUnavailableScreen({
    title = "Unavailable on this domain",
    message = "This website is only available after the vendor domain is active.",
}) {
    return (
        <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center px-4 text-center">
            <div className="max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-slate-200">
                <div className="text-lg font-bold">{title}</div>
                <p className="mt-2 text-sm text-slate-400">{message}</p>
            </div>
        </div>
    );
}

// All phone verification uses the authenticated incoming-text flow.
function GlobalVerifyPhoneHost() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const show = () => setOpen(true);
        window.addEventListener("open-phone-verify", show);
        return () => window.removeEventListener("open-phone-verify", show);
    }, []);
    const clubContext = isTenantCandidateHost(window.location.hostname) || /^\/(v|clubs)(\/|$)/.test(window.location.pathname);
    const token = clubContext ? localStorage.getItem("clubsClientToken") : localStorage.getItem("sessionId");
    return open ? <Suspense fallback={null}><FanytelVerifyPhoneModal open token={token || undefined}
        onClose={() => setOpen(false)}
        onVerified={() => { setOpen(false); window.dispatchEvent(new CustomEvent("phone-verify-success")); }} />
    </Suspense> : null;
}

// Global Signup Prompt Host
// Listens for 'open-signup-prompt' custom event
function GlobalSignupPromptHost() {
    const [showSignup, setShowSignup] = useState(false);
    const [signupAction, setSignupAction] = useState(null);
    const [signupReturnTo, setSignupReturnTo] = useState(null);

    useEffect(() => {
        const handleOpenSignup = (event) => {
            const { action, returnTo } = event.detail || {};
            setSignupAction(action || null);
            setSignupReturnTo(returnTo || window.location.pathname);
            setShowSignup(true);
        };

        window.addEventListener("open-signup-prompt", handleOpenSignup);
        return () => {
            window.removeEventListener("open-signup-prompt", handleOpenSignup);
        };
    }, []);

    if (!showSignup) return null;

    return (
        <Suspense fallback={null}>
            <SignupPromptDialog
                open
                onClose={() => setShowSignup(false)}
                action={signupAction}
                returnTo={signupReturnTo}
            />
        </Suspense>
    );
}

// Route guard: redirects to /home with toast if capability is disabled
function GatedRoute({ capability, children }) {
    const navigate = useNavigate();
    useEffect(() => {
        if (!hasCapability(capability)) {
            toast.error("Feature unavailable", {
                description: `${capability} is not enabled.`,
            });
            navigate("/home", { replace: true });
        }
    }, [capability, navigate]);
    if (!hasCapability(capability)) return null;
    return children;
}

// Wrapper to conditionally show BottomNav
// MobileVerificationOverlay is now rendered inside TopHeader (Navigation.jsx)
function VendorSignupRedirect() {
    // Legacy /v/:vendorSlug/signup → new global signup with auto-join.
    // Preserve any referral parameters (ref, rtoken) and the original query
    // string so per-club referral links keep working — previously these
    // got dropped, so a player who clicked a "Refer & Earn" link from the
    // clubs UI lost their referrer attribution on signup.
    const { vendorSlug } = useParams();
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    search.set("join", vendorSlug);
    return <Navigate to={`/signup?${search.toString()}`} replace />;
}

function RedirectVendorToAdmin() {
    // Legacy /vendor/* → /admin/* (terminology rename).
    const location = useLocation();
    const newPath = location.pathname.replace(/^\/vendor/, "/admin");
    return <Navigate to={`${newPath}${location.search || ""}`} replace />;
}

function AppContent() {
    const location = useLocation();
    const navigate = useNavigate();
    const currentHostname =
        typeof window !== "undefined" ? window.location.hostname : "";
    const tenantCandidateHost =
        typeof window !== "undefined" && isTenantCandidateHost(currentHostname);
    const tenantRoutingActive =
        tenantCandidateHost &&
        !allowsTenantSuperadminRoute(currentHostname, location.pathname);
    const [tenantHostState, setTenantHostState] = useState(() => ({
        loading: tenantRoutingActive,
        checked: !tenantRoutingActive,
        vendor: null,
    }));
    const [tenantResolveAttempt, setTenantResolveAttempt] = useState(0);
    const tenantEntryShouldResolve =
        tenantRoutingActive && shouldRedirectTenantEntry(location);
    const [tenantEntryResolving, setTenantEntryResolving] = useState(
        () => tenantEntryShouldResolve,
    );

    useEffect(() => {
        if (!tenantRoutingActive) {
            setTenantHostState({ loading: false, checked: true, vendor: null });
            return undefined;
        }
        let cancelled = false;
        const controller = new AbortController();
        setTenantHostState((prev) => ({ ...prev, loading: true, error: false }));
        (async () => {
            try {
                const data = await resolveTenantHost({ signal: controller.signal });
                const vendor =
                    data?.vendor?.status === "active" ? data.vendor : null;
                if (!cancelled) {
                    setTenantHostState({
                        loading: false,
                        checked: true,
                        vendor,
                    });
                }
            } catch {
                if (!cancelled) {
                    setTenantHostState({
                        loading: false,
                        checked: true,
                        vendor: null,
                        error: true,
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [tenantRoutingActive, tenantResolveAttempt]);

    // Capture referral token from any URL. Token (rtoken) is the primary path.
    // Raw code (ref) kept only for backward compatibility with old links.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const rtoken = params.get("rtoken");
        const ref = params.get("ref");
        if (rtoken) {
            localStorage.setItem("pendingReferralToken", rtoken);
            localStorage.removeItem("pendingReferralCode");
        } else if (ref) {
            localStorage.setItem("pendingReferralCode", ref);
            localStorage.removeItem("pendingReferralToken");
        }
    }, [location.search]);

    useEffect(() => {
        if (!tenantEntryShouldResolve) {
            setTenantEntryResolving(false);
            return undefined;
        }
        if (tenantHostState.loading || !tenantHostState.checked) {
            setTenantEntryResolving(true);
            return undefined;
        }
        const slug = tenantHostState.vendor?.slug || null;
        if (!slug) {
            setTenantEntryResolving(false);
            return undefined;
        }

        setTenantEntryResolving(true);
        const params = new URLSearchParams(location.search || "");
        params.delete("join");
        params.delete("code");
        try {
            localStorage.setItem("clubsLastSlug", slug);
            window.dispatchEvent(
                new CustomEvent("clubs-active-changed", {
                    detail: { slug },
                }),
            );
        } catch {
            /* noop */
        }
        // Custom-domain tenant routes should never expose the shared
        // `/clubs/...` URLs. The host itself implies the vendor, so keep the
        // address bar on clean tenant paths while preserving referral params.
        const hasClientSession = !!localStorage.getItem("clubsClientToken");
        const wantsLogin =
            location.pathname === "/login" ||
            location.pathname === "/clubs/login";
        const cleanPath = hasClientSession
            ? "/"
            : wantsLogin
              ? "/login"
              : location.pathname === "/"
                ? "/"
                : "/signup";
        const target = `${cleanPath}${params.toString() ? `?${params.toString()}` : ""}`;
        const current = `${location.pathname || "/"}${location.search || ""}`;
        setTenantEntryResolving(false);
        if (target !== current) {
            navigate(target, { replace: true });
        }
        return undefined;
    }, [
        tenantEntryShouldResolve,
        tenantHostState.checked,
        tenantHostState.loading,
        tenantHostState.vendor,
        location.pathname,
        location.search,
        navigate,
    ]);

    if (
        tenantRoutingActive &&
        (tenantHostState.loading || (tenantEntryResolving && tenantEntryShouldResolve))
    ) {
        return (
            <>
                <LuckyVaultLaunch
                    key="tenant-launch"
                    tenant={tenantHostState.vendor}
                />
                <PageLoading quiet onRetry={() => setTenantResolveAttempt((value) => value + 1)} />
            </>
        );
    }

    if (tenantRoutingActive && tenantHostState.error) {
        return <PageLoadRecovery title="We couldn't connect to your club" message="Check your connection and try again. Your sign-in is kept." onRetry={() => setTenantResolveAttempt((value) => value + 1)} />;
    }

    if (
        tenantRoutingActive &&
        tenantHostState.checked &&
        !tenantHostState.vendor
    ) {
        return <TenantUnavailableScreen />;
    }

    if (
        tenantRoutingActive &&
        (location.pathname.startsWith("/superadmin") ||
            location.pathname.startsWith("/support") ||
            location.pathname === "/plans" ||
            location.pathname.startsWith("/plans/") ||
            location.pathname === "/clubs/explore" ||
            location.pathname.startsWith("/clubs/explore/"))
    ) {
        return (
            <TenantUnavailableScreen message="Public Multiple Games pages are not available inside a vendor domain." />
        );
    }

    if (tenantRoutingActive) {
        return (
            <>
                <LuckyVaultLaunch
                    key="tenant-launch"
                    tenant={tenantHostState.vendor}
                    nativeReady
                />
                <LuckyVaultMetadata />
                <Suspense fallback={<RouteChunkFallback tenant />}>
                    <PresetShell>
                        <Routes>
                            <Route path="/admin/*" element={<VendorPanel />} />
                            <Route
                                path="/vendor/*"
                                element={<RedirectVendorToAdmin />}
                            />
                            <Route
                                path="/auth/forgot-password"
                                element={<ForgotPasswordPage />}
                            />
                            <Route
                                path="/clubs/*"
                                element={<TenantLegacyClubsRedirect />}
                            />
                            <Route
                                path="/download"
                                element={<LuckyVaultGetAppPage />}
                            />
                            <Route
                                path="/get-app"
                                element={<Navigate to="/download" replace />}
                            />
                            <Route path="/*" element={<ClubsApp />} />
                        </Routes>
                    </PresetShell>
                </Suspense>

                <LuckyVaultInstallPrompt />
                <GlobalVerifyPhoneHost />
            </>
        );
    }

    const hideNav =
        hideNavPaths.includes(location.pathname) ||
        location.pathname.startsWith("/admin") ||
        location.pathname.startsWith("/superadmin") ||
        location.pathname.startsWith("/support") ||
        location.pathname.startsWith("/vendor") ||
        location.pathname.startsWith("/v/") ||
        location.pathname.startsWith("/clubs") ||
        location.pathname.startsWith("/c/") ||
        location.pathname === "/login" ||
        location.pathname === "/signup";

    return (
        <>
            <Suspense fallback={<RouteChunkFallback />}>
                <PresetShell>
                    <Routes>
                        {/* Public Checkout Portal - No auth required */}
                        <Route
                            path="/c/:publicCheckoutToken"
                            element={<PublicCheckoutPage />}
                        />

                        {/* Client Routes - Now preset-aware */}
                        {/* HARDCODED: "/" routes to the clubs (vendor-system client) UI. */}
                        <Route
                            path="/"
                            element={<Navigate to="/clubs" replace />}
                        />

                        <Route
                            path="/landing"
                            element={
                                <PresetPage
                                    slot="landing"
                                    fallback={<LandingPage />}
                                />
                            }
                        />

                        {/* User Dashboard */}
                        <Route
                            path="/home"
                            element={
                                <PresetPage
                                    slot="home"
                                    fallback={<HomePage />}
                                />
                            }
                        />

                        {/* Games Catalog & Detail Pages */}
                        <Route
                            path="/games"
                            element={
                                <PresetPage
                                    slot="games"
                                    fallback={<GamesPage />}
                                />
                            }
                        />
                        <Route
                            path="/games/:gameId"
                            element={
                                <PresetPage
                                    slot="game_detail"
                                    fallback={<GameDetailPage />}
                                />
                            }
                        />

                        {/* Wallet & Transactions */}
                        <Route
                            path="/wallet"
                            element={
                                <PresetPage
                                    slot="wallet"
                                    fallback={<WalletPage />}
                                />
                            }
                        />
                        <Route
                            path="/wallet/gateway-status"
                            element={<GatewayDepositStatus />}
                        />
                        <Route
                            path="/transactions"
                            element={
                                <PresetPage
                                    slot="transactions"
                                    fallback={<TransactionHistoryPage />}
                                />
                            }
                        />
                        <Route
                            path="/referrals"
                            element={
                                <PresetPage
                                    slot="referrals"
                                    fallback={<ReferralsPage />}
                                />
                            }
                        />
                        <Route
                            path="/achievements"
                            element={
                                <PresetPage
                                    slot="rewards"
                                    fallback={<AchievementsPage />}
                                />
                            }
                        />
                        <Route
                            path="/leaderboards"
                            element={
                                <Navigate
                                    to="/achievements?tab=leaderboards"
                                    replace
                                />
                            }
                        />
                        <Route
                            path="/inbox"
                            element={
                                <PresetPage
                                    slot="inbox"
                                    fallback={<InboxPage />}
                                />
                            }
                        />
                        <Route
                            path="/messages"
                            element={
                                <GatedRoute capability="client.chat">
                                    <PresetPage
                                        slot="messages"
                                        fallback={
                                            <Suspense
                                                fallback={
                                                    <div className="flex items-center justify-center min-h-[60vh] text-gray-400 text-sm">
                                                        Loading chat…
                                                    </div>
                                                }
                                            >
                                                <ChatPage />
                                            </Suspense>
                                        }
                                    />
                                </GatedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <PresetPage
                                    slot="profile"
                                    fallback={<ProfilePage />}
                                />
                            }
                        />
                        <Route
                            path="/verification"
                            element={
                                <PresetPage
                                    slot="verification"
                                    fallback={<VerificationPage />}
                                />
                            }
                        />
                        <Route
                            path="/terms"
                            element={
                                <PresetPage
                                    slot="terms"
                                    fallback={<TermsPage />}
                                />
                            }
                        />
                        <Route
                            path="/privacy"
                            element={
                                <PresetPage
                                    slot="privacy"
                                    fallback={<PrivacyPage />}
                                />
                            }
                        />

                        {/* Auth Routes — unified to /login and /signup */}
                        <Route
                            path="/login"
                            element={
                                <RedirectIfLoggedIn>
                                    <UnifiedLoginPage />
                                </RedirectIfLoggedIn>
                            }
                        />
                        <Route
                            path="/signup"
                            element={
                                <RedirectIfLoggedIn>
                                    <UnifiedSignupPage />
                                </RedirectIfLoggedIn>
                            }
                        />
                        <Route
                            path="/auth/login"
                            element={<Navigate to="/login" replace />}
                        />
                        <Route
                            path="/auth/signup"
                            element={<Navigate to="/signup" replace />}
                        />
                        {/* /clubs/login and /clubs/signup are handled by
                            ClubsApp's own auth pages so opening the public
                            client portal never bounces an existing admin
                            session to /superadmin. Query params (?ref=,
                            ?join=, ?next=) also survive through the wildcard
                            `/clubs/*` route below. */}
                        <Route
                            path="/vendor/login"
                            element={<Navigate to="/admin/login" replace />}
                        />
                        <Route
                            path="/auth/magic-link"
                            element={
                                <RedirectIfLoggedIn>
                                    <PresetPage
                                        slot="auth_magic"
                                        fallback={<MagicLinkPage />}
                                    />
                                </RedirectIfLoggedIn>
                            }
                        />
                        <Route
                            path="/auth/set-password"
                            element={
                                <RedirectIfLoggedIn>
                                    <PresetPage
                                        slot="auth_setpassword"
                                        fallback={<SetPasswordPage />}
                                    />
                                </RedirectIfLoggedIn>
                            }
                        />
                        <Route
                            path="/auth/forgot-password"
                            element={
                                <RedirectIfLoggedIn>
                                    <PresetPage
                                        slot="auth_forgot"
                                        fallback={<ForgotPasswordPage />}
                                    />
                                </RedirectIfLoggedIn>
                            }
                        />

                        {/* Rules/Info Routes — preset-aware */}
                        <Route
                            path="/rules/how-it-works"
                            element={
                                <PresetPage
                                    slot="rules_how"
                                    fallback={<HowItWorksPage />}
                                />
                            }
                        />
                        <Route
                            path="/rules/referrals"
                            element={
                                <PresetPage
                                    slot="rules_referrals"
                                    fallback={<ReferralRulesPage />}
                                />
                            }
                        />
                        <Route
                            path="/rules/tiers"
                            element={
                                <PresetPage
                                    slot="rules_tiers"
                                    fallback={<TierSystemPage />}
                                />
                            }
                        />
                        <Route
                            path="/rules/bonuses"
                            element={
                                <PresetPage
                                    slot="rules_bonuses"
                                    fallback={<BonusesPage />}
                                />
                            }
                        />
                        <Route
                            path="/rules/payments"
                            element={
                                <PresetPage
                                    slot="rules_payments"
                                    fallback={<PaymentsPage />}
                                />
                            }
                        />

                        {/* Per-role login URLs.
                         * /admin/login → vendor-branded login (handled by
                         *   <VendorPanel/> below which has its own internal
                         *   <Route path="login" element={<VendorLoginPage/>}>).
                         * /superadmin/login → dedicated master admin portal
                         *   login (populates AdminContext directly via the
                         *   useAdmin() hook, then lands on /superadmin). */}
                        <Route
                            path="/superadmin/login"
                            element={
                                <RedirectIfLoggedIn>
                                    <AdminLoginPage />
                                </RedirectIfLoggedIn>
                            }
                        />

                        {/* Protected Superadmin Routes (was /admin) */}
                        <Route
                            path="/superadmin"
                            element={<ProtectedAdminLayout />}
                        >
                            {/* Dashboard & Analytics */}
                            <Route index element={<Dashboard />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            {/* Analytics v2 — vendor-aware (added 2026-05-08) */}
                            <Route
                                path="analytics-overview"
                                element={<VendorAnalyticsPage />}
                            />
                            <Route
                                path="vendor-analytics"
                                element={<VendorAnalyticsPage />}
                            />
                            <Route
                                path="vendor-leaderboard"
                                element={<VendorLeaderboardPage />}
                            />
                            <Route
                                path="analytics-cohorts"
                                element={<AnalyticsCohortPage />}
                            />
                            <Route
                                path="analytics-funnel"
                                element={<AnalyticsFunnelPage />}
                            />
                            <Route path="finance" element={<FinancePage />} />
                            <Route
                                path="transactions"
                                element={<TransactionsPage />}
                            />
                            <Route
                                path="gateway-disputes"
                                element={<GatewayDisputesPage />}
                            />
                            <Route
                                path="game-analytics"
                                element={<GameAnalyticsPage />}
                            />
                            <Route
                                path="new-depositors"
                                element={<NewDepositorsPage />}
                            />
                            <Route
                                path="users-analytics"
                                element={<UsersAnalyticsPage />}
                            />
                            <Route
                                path="users-profitability"
                                element={<UserProfitabilityPage />}
                            />
                            {/* /verified-purchases route retired 2026-05-08 — falls back to legacy redirect to player money */}
                            <Route
                                path="verified-purchases"
                                element={
                                    <Navigate
                                        to="/superadmin/users-profitability"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="verified-users"
                                element={<VerifiedUsersPage />}
                            />
                            <Route
                                path="ops-console"
                                element={<OpsConsolePage />}
                            />

                            {/* Payments */}
                            <Route
                                path="payment-methods"
                                element={<PaymentMethodsPage />}
                            />
                            <Route
                                path="deposits"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="withdrawals"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            {/* Legacy payment requests — fully retired 2026-05-08 → unified Player Money queue */}
                            <Route
                                path="payment-requests"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="requests"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />

                            {/* Games */}
                            <Route path="games" element={<GamesAdminPage />} />
                            <Route
                                path="game-providers"
                                element={<GameProvidersPage />}
                            />
                            <Route
                                path="categories"
                                element={<CategoriesAdminPage />}
                            />

                            {/* Platform */}
                            <Route
                                path="promotions"
                                element={<PromotionsPage />}
                            />
                            <Route
                                path="manual-free-credits"
                                element={<ManualFreeCreditsPage />}
                            />
                            <Route
                                path="announcements"
                                element={<AnnouncementsPage />}
                            />
                            <Route
                                path="achievements"
                                element={<AchievementsAdminPage />}
                            />
                            <Route
                                path="referral-tiers"
                                element={<ReferralTiersPage />}
                            />
                            <Route path="activity-commissions" element={<ActivityCommissionsPage />} />
                            <Route path="levels" element={<LevelsPage />} />
                            <Route
                                path="leaderboards"
                                element={
                                    hasCapability("admin.leaderboards") ? (
                                        <AdminLeaderboardConfigPage />
                                    ) : (
                                        <Navigate to="/admin" replace />
                                    )
                                }
                            />
                            <Route
                                path="winners-feed"
                                element={
                                    hasCapability("admin.winnersFeed") ? (
                                        <AdminWinnersFeedPage />
                                    ) : (
                                        <Navigate to="/admin" replace />
                                    )
                                }
                            />
                            <Route
                                path="referral-analytics"
                                element={<ReferralAnalyticsPage />}
                            />
                            <Route
                                path="referral-payout-forecast"
                                element={<ReferralPayoutForecastPage />}
                            />
                            <Route
                                path="messaging"
                                element={<MessagingCenterPage />}
                            />
                            <Route path="chat" element={<AdminChatPage />} />
                            <Route
                                path="voided-amounts"
                                element={<VoidedAmountsPage />}
                            />
                            <Route path="rules" element={<RulesPage />} />
                            <Route
                                path="wallet-rules"
                                element={<WalletRulesPage />}
                            />
                            {/* FIX #4: Notifications route removed - nonfunctional page */}
                            {/* <Route path="notifications" element={<NotificationsPage />} /> */}

                            {/* Integrations - Consolidated to single page */}
                            <Route
                                path="integrations"
                                element={<IntegrationsPage />}
                            />
                            {/* PHASE 1: Redirect legacy integration pages to canonical /admin/integrations */}
                            <Route
                                path="integrations-settings"
                                element={
                                    <Navigate
                                        to="/admin/integrations"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="integrations-setup"
                                element={
                                    <Navigate
                                        to="/admin/integrations"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="integration-credentials"
                                element={
                                    <Navigate
                                        to="/admin/integrations"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="webhook-events"
                                element={<WebhookEventsPage />}
                            />
                            <Route path="webhooks" element={<WebhooksPage />} />
                            {/* Legacy reviewer-targets route redirects to canonical Telegram Targets */}
                            <Route
                                path="reviewer-targets"
                                element={
                                    <Navigate
                                        to="/admin/telegram-targets"
                                        replace
                                    />
                                }
                            />

                            {/* Phase 6: Migration */}
                            <Route
                                path="alias-migration"
                                element={<AliasMigrationPage />}
                            />

                            {/* Admin */}
                            <Route path="users" element={<UsersPage />} />
                            <Route path="security" element={<SecurityPage />} />
                            <Route
                                path="audit-logs"
                                element={<AuditLogsPage />}
                            />
                            <Route
                                path="permissions"
                                element={<PermissionsPage />}
                            />
                            <Route
                                path="permissions-matrix"
                                element={<PermissionsMatrixPage />}
                            />
                            <Route
                                path="staff-management"
                                element={<StaffManagementPage />}
                            />

                            {/* Phase 11+: Engine-based Orders/Payments/Payouts */}
                            <Route path="orders" element={<OrdersPage />} />
                            {/* Unified Player Money page — replaces Deposits/Withdrawals/Platform Methods */}
                            <Route
                                path="player-money"
                                element={<PlayerMoneyPage />}
                            />
                            <Route
                                path="safe-wallet-reviews"
                                element={<SafeWalletReviewsPage />}
                            />
                            {/* Legacy redirects → unified page */}
                            <Route
                                path="payments-review"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="payouts-review"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="telegram-targets"
                                element={<AdminTelegramPage />}
                            />

                            {/* Appearance Presets — client preset-based frontend system */}
                            <Route
                                path="appearance-presets"
                                element={<FrontendAppearancePresetsPage />}
                            />

                            {/* Vendors (Multi-tenant) */}
                            <Route path="vendors" element={<VendorsPage />} />
                            <Route
                                path="vendors/:vendorId"
                                element={<VendorDetailPage />}
                            />

                            {/* Session 4 P0 — platform-routed payment review queue + admin global telegram */}
                            <Route
                                path="payment-reviews"
                                element={
                                    <Navigate
                                        to="/superadmin/player-money"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="platform-ledger"
                                element={<AdminPlatformLedgerPage />}
                            />
                            <Route
                                path="vendor-topups"
                                element={<AdminVendorTopupsPage />}
                            />
                            <Route
                                path="telegram"
                                element={<AdminTelegramPage />}
                            />
                            {/* Legacy single-bot config — still loadable but deprecated;
                                the merged AdminTelegramPage is now the canonical Telegram page. */}
                            <Route
                                path="telegram-legacy-config"
                                element={<AdminPlatformTelegramPage />}
                            />
                        </Route>

                        {/* Support Console - chat-only surface for admin + support roles.
                        Same login credentials (admin session) but a dedicated URL
                        without the full admin sidebar. */}
                        <Route
                            path="/support"
                            element={
                                <SupportRouteGuard>
                                    <SupportLayout />
                                </SupportRouteGuard>
                            }
                        >
                            <Route index element={<SupportChatShell />} />
                        </Route>

                        {/* Multi-tenant Admin Panel (was /vendor) */}
                        <Route path="/plans" element={<PlansPage />} />
                        <Route path="/admin/*" element={<VendorPanel />} />
                        <Route
                            path="/vendor/*"
                            element={<RedirectVendorToAdmin />}
                        />

                        {/* Global Clubs portal — light, multi-club client UI */}
                        <Route path="/clubs/*" element={<ClubsApp />} />

                        {/* Multi-tenant Client Portal (per-vendor URL) — keep
                            backward compatible. Old /v/:slug/signup links
                            redirect into the global signup with auto-join. */}
                        <Route
                            path="/v/:vendorSlug/signup"
                            element={<VendorSignupRedirect />}
                        />
                        <Route
                            path="/v/:vendorSlug/*"
                            element={<ClientPortal />}
                        />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </PresetShell>
            </Suspense>

            {/* Global Phone Verification Modal Host */}
            <GlobalVerifyPhoneHost />

            {/* Global Signup Prompt Host */}
            <GlobalSignupPromptHost />
        </>
    );
}

function App() {
    const [nonCriticalReady, setNonCriticalReady] = useState(false);
    useEffect(() => startAppUsageTracking(), []);
    useEffect(() => startPushOpenTracking(), []);

    // Push setup and its permission prompt are useful, but neither belongs on
    // the critical rendering path. Wait until the page has loaded and the
    // browser has idle time so first paint, hydration and the opening motion do
    // not compete for the main thread on lower-end phones.
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        let cancelled = false;
        let idleHandle = null;
        let timerHandle = null;

        const initialize = async () => {
            if (cancelled) return;
            setNonCriticalReady(true);
            try {
                const { initializePushNotifications } = await import(
                    "@/services/pushNotificationService"
                );
                const result = await initializePushNotifications();
                if (result.success) {
                    console.log("[App] Push notifications initialized");
                }
            } catch (error) {
                console.error("[App] Push notification init error:", error);
            }
        };

        const schedule = () => {
            if ("requestIdleCallback" in window) {
                idleHandle = window.requestIdleCallback(initialize, {
                    timeout: 4000,
                });
            } else {
                timerHandle = window.setTimeout(initialize, 1800);
            }
        };

        if (document.readyState === "complete") schedule();
        else window.addEventListener("load", schedule, { once: true });

        return () => {
            cancelled = true;
            window.removeEventListener("load", schedule);
            if (idleHandle !== null && "cancelIdleCallback" in window) {
                window.cancelIdleCallback(idleHandle);
            }
            if (timerHandle !== null) window.clearTimeout(timerHandle);
        };
    }, []);

    // One-shot global gesture listener so the chat notification sound
    // works on any page (vendor portal, clubs portal, admin) — browsers
    // require a user gesture before AudioContext can resume.
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        let done = false;
        const handler = () => {
            if (done) return;
            done = true;
            try {
                unlockNotificationSound();
            } catch {
                /* ignore */
            }
            window.removeEventListener("pointerdown", handler);
            window.removeEventListener("keydown", handler);
        };
        window.addEventListener("pointerdown", handler, { once: false });
        window.addEventListener("keydown", handler, { once: false });
        return () => {
            window.removeEventListener("pointerdown", handler);
            window.removeEventListener("keydown", handler);
        };
    }, []);

    return (
        <AppProvider>
            <AdminProvider>
                <PermissionsProvider>
                    <PresetProvider>
                        <div className="App min-h-screen bg-[#0a0a0a] text-zinc-100">
                            <BrowserRouter>
                                <AppContent />
                            </BrowserRouter>

                            <ConnectionStatusBanner />

                            {nonCriticalReady ? (
                                <Suspense fallback={null}>
                                    <AutoPushNotificationPrompt />
                                </Suspense>
                            ) : null}

                            {/* Premium Toast Container */}
                            <Toaster
                                position="top-center"
                                richColors
                                closeButton={!isHittingSlotsHost()}
                                duration={isHittingSlotsHost() ? 4200 : undefined}
                                gap={8}
                            />
                        </div>
                    </PresetProvider>
                </PermissionsProvider>
            </AdminProvider>
        </AppProvider>
    );
}

export default App;
